// Basic
import { useEffect } from 'react';
import PropTypes from 'prop-types';
// Libs
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@/common-utils/libraries/tailwind/utils';
// Components
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/common-utils/components/ui/form';
import Entity from '@/common-utils/components/renders/entity/Entity';
import { Input } from '@/common-utils/components/ui/input';
import { DateInput } from '@/common-utils/components/ui/dateInput';
// Common
import subscriptionFields from '@/app/modules/subscriptions/fields/subscriptionFields';
import getObjectAttributeFromStringKey from '@/common-utils/functions/getObjectAttributeFromStringKey';
import generateNestedFieldAccessor from '@/common-utils/functions/generateNestedFieldAccessor';
// Lang
import { nsEnvelope } from '@/app/i18n/namespaces';

/**
 * @param {string} client
 * @param {Object} posTerminals
 * @param {function} setFormData
 * @param {function} setFormValidation
 * @param {function} onFormSubmit
 * @returns {JSX}
 */
function CreateEnvelopeForm({ client, posTerminals, setFormData, setFormValidation, onFormSubmit }) {
    // Constants
    const posTerminalSerial = posTerminals.length === 1 &&
        getObjectAttributeFromStringKey(posTerminals[0], generateNestedFieldAccessor(subscriptionFields.POS_TERMINAL.SERIAL_NUMBER));

    // Form validation
    const accessCodeSchema = z
        .string()
        .refine((accessCode) => /^\d{4,9}$/.test(accessCode), { message: '' });

    const ipServerSchema = z
        .string()
        .refine((ip) =>
            // eslint-disable-next-line max-len
            /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]\d|\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]\d|\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]\d|\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]\d|\d):([1-9]\d{0,3}|[1-5]\d{4}|6[0-4]\d{3}|65[0-4]\d{2}|655[0-2]\d|6553[0-5])$/
                .test(ip), {
            message: ''
        });

    const formSchema = z.object({
        scheduledStartDate: z.date().optional(),
        scheduledEndDate: z.date().optional(),
        accessCode: accessCodeSchema,
        ipServer: ipServerSchema
    })
        .superRefine((values, ctx) => {
            const { scheduledStartDate, scheduledEndDate } = values;

            // Start and end dates are both empty =>  valid
            if (!scheduledStartDate && !scheduledEndDate) {
                return;
            }

            // If only end date is set => valid
            if (!scheduledStartDate && scheduledEndDate) {
                return;
            }

            // If start date is set without end date => Error
            if (scheduledStartDate && !scheduledEndDate) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['scheduledEndDate']
                });
                return;
            }

            // If both dates exist but with a wrong order => Error
            if (scheduledStartDate >= scheduledEndDate) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['scheduledStartDate']
                });
            }
        });

    // Form definition
    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: {
            scheduledStartDate: undefined,
            scheduledEndDate: undefined,
            accessCode: '',
            ipServer: ''
        }
    });

    // Hook
    useEffect(() => {
        // Trigger validation
        form.trigger(['scheduledStartDate', 'scheduledEndDate', 'accessCode', 'ipServer']);

        setFormValidation(form.formState.isValid);
        setFormData(form.getValues());
    }, [
        form.watch('scheduledStartDate'),
        form.watch('scheduledEndDate'),
        form.watch('accessCode'),
        form.watch('ipServer'),
        form.formState.isValid]
    );

    return (
        <div>
            <div className={cn('mb-4')}>
                <Entity namespace={nsEnvelope} entity='keys.envelopes.client' />
                <div>{client}</div>
            </div>
            <div className={cn('mb-4')}>
                {posTerminals.length > 1 &&
                    <Entity
                        namespace={nsEnvelope}
                        entity='modal.create_envelopes.terminal_serial_numbers'
                        data={{ numberOfSelectedItems: posTerminals.length }}
                    />
                }
                {posTerminals.length === 1 &&
                    <>
                        <Entity
                            namespace={nsEnvelope}
                            entity='keys.envelopes.serial_number'
                        />
                        <div>{posTerminalSerial}</div>
                    </>
                }
            </div>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onFormSubmit)} className='update-password-form space-y-2'>
                    <FormField
                        control={form.control}
                        name='scheduledStartDate'
                        render={({ field, fieldState }) => (
                            <FormItem>
                                <FormLabel>
                                    <Entity namespace={nsEnvelope} entity='keys.envelopes.schedule_start' />
                                </FormLabel>
                                <FormControl>
                                    <DateInput
                                        selected={field.value}
                                        onSelect={(date) => field.onChange(date)}
                                        placeholder={{ namespace: nsEnvelope, entity: 'form.placeholder.start_date' }}
                                        disableBeforeToday={true}
                                        className={fieldState.error ? 'border border-danger' : ''}
                                    />
                                </FormControl>
                            </FormItem>
                        )
                        }
                    />
                    <FormField
                        control={form.control}
                        name='scheduledEndDate'
                        render={({ field, fieldState }) => (
                            <FormItem>
                                <FormLabel>
                                    <Entity namespace={nsEnvelope} entity='keys.envelopes.schedule_end' />
                                </FormLabel>
                                <FormControl>
                                    <DateInput
                                        selected={field.value}
                                        onSelect={(date) => field.onChange(date)}
                                        placeholder={{ namespace: nsEnvelope, entity: 'form.placeholder.end_date' }}
                                        disableBeforeToday={true}
                                        className={fieldState.error ? 'border border-danger' : ''}
                                    />
                                </FormControl>
                            </FormItem>
                        )
                        }
                    />
                    <FormField
                        control={form.control}
                        name='accessCode'
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel required>
                                    <Entity namespace={nsEnvelope} entity='keys.envelopes.access_code' />
                                </FormLabel>
                                <FormControl>
                                    <Input
                                        type='text'
                                        autoComplete='off'
                                        entity='form.placeholder.access_code'
                                        namespace={nsEnvelope}
                                        {...field} />
                                </FormControl>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name='ipServer'
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel required>
                                    <Entity namespace={nsEnvelope} entity='keys.envelopes.server_ip' />
                                </FormLabel>
                                <FormControl>
                                    <Input
                                        type='text'
                                        autoComplete='off'
                                        entity='form.placeholder.server_ip'
                                        namespace={nsEnvelope}
                                        {...field} />
                                </FormControl>
                            </FormItem>
                        )}
                    />
                </form>
            </Form>
        </div>
    );
}

CreateEnvelopeForm.propTypes = {
    client: PropTypes.string.isRequired,
    posTerminals: PropTypes.array.isRequired,
    setFormData: PropTypes.func.isRequired,
    setFormValidation: PropTypes.func.isRequired,
    onFormSubmit: PropTypes.func.isRequired
};

export default CreateEnvelopeForm;